import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BotsService } from '@core/bots';
import { Pagination } from '@root/shared/abstraction';
import { DEFAULT_PAGINATION_PARAMS } from '@root/shared/table';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  map,
  Observable,
  ReplaySubject,
  shareReplay,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { FeedbacksApiService } from '.';
import { FeedbacksReq } from './feedbacks.abstraction';
import { ToastService, ToastType } from '@pin-up-global/common';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class FeedbacksService {
  #feedbacksApiService = inject(FeedbacksApiService);
  #translateService = inject(TranslateService);
  #toastService = inject(ToastService);
  #botsService = inject(BotsService);
  #router = inject(Router);

  #filters = new ReplaySubject<Partial<FeedbacksReq>>();
  #update$ = new BehaviorSubject<void>(void 0);
  #feedbacksRes$ = this.#update$.pipe(
    switchMap(() => combineLatest([this.#filters, this.#botsService.selectedBotId$])),
    debounceTime(500),
    switchMap(([filters, botId]) => this.#feedbacksApiService.getFeedbacks({ ...filters, botId })),
    shareReplay(1)
  );
  readonly feedbacks$ = this.#feedbacksRes$.pipe(map((res) => res.items));

  readonly pagination$: Observable<Pagination> = this.#feedbacksRes$.pipe(
    map((res) => res.count),
    withLatestFrom(this.#filters),
    map(([count, filters]) => ({
      count,
      page: filters.page!,
      limit: filters.limit!,
    })),
    startWith(DEFAULT_PAGINATION_PARAMS)
  );

  readonly isLoading$ = this.#feedbacksApiService.isLoading$;

  updateFilters(filters: Partial<FeedbacksReq>): void {
    this.#filters.next(filters);
  }

  refresh(): void {
    this.#update$.next();
  }

  resetFilters(): void {
    this.#router.navigate([], {
      relativeTo: this.#router.routerState.root,
      queryParams: {},
    });
  }

  archiveFeedback(id: number): Observable<void> {
    return this.#feedbacksApiService.archiveFeedback(id).pipe(
      tap(() => {
        this.#toastService.show({
          type: ToastType.Success,
          title: this.#translateService.instant('feedbacksPage.toast.feedbackArchived'),
        });
        this.refresh();
      }),
    );
  }
}
